import { useEffect, useState } from "react";
import { Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { formatBalance, formatIsoDay, formatTRY } from "@/lib/format";
import { EmptyRow, LoadingRow } from "@/components/common/States";
import { DocLink } from "@/components/common/DocLink";
import StatementShare from "@/components/customers/StatementShare";
import { useNavigate } from "react-router-dom";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const PRESETS = [["Bu Ay", () => { const n = new Date(); return [iso(new Date(n.getFullYear(), n.getMonth(), 1)), iso(n)]; }],
  ["Geçen Ay", () => { const n = new Date(); return [iso(new Date(n.getFullYear(), n.getMonth() - 1, 1)), iso(new Date(n.getFullYear(), n.getMonth(), 0))]; }],
  ["Son 3 Ay", () => { const n = new Date(); return [iso(new Date(n.getFullYear(), n.getMonth() - 2, 1)), iso(n)]; }],
  ["Bu Yıl", () => { const n = new Date(); return [iso(new Date(n.getFullYear(), 0, 1)), iso(n)]; }],
  ["Tümü", () => ["", ""]]];

// Sunucu ekstre PDF'iyle (services/statement_pdf.DOC) aynı etiketler
const DOC = { acilis: "Açılış", fatura: "Fatura", tahsilat: "Tahsilat", iade: "İade", cek: "Çek", senet: "Senet", duzeltme: "Düzeltme", diger: "Diğer", karsiliksiz: "Karşılıksız",
  tahsilat_iptal: "Tahsilat İptali", virman: "Virman", mal_kabul: "Mal Kabul", alis: "Alış", alis_iade: "Alış İadesi", alis_iade_tahsil: "Alış İadesi Tahsilatı", alis_iade_iptal: "Alış İadesi İptali",
  cari_odeme: "Ödeme", ciro: "Ciro", ciro_iade: "Ciro İadesi", evrak_iade: "Evrak İadesi", insaat: "İnşaat" };

// Satır tıklaması yalnız detay sayfası olan belgelerde (yazdırma/makbuz görünümleri hariç)
const detail = (link) => (link && !link.includes("/yazdir") && !link.includes("/makbuz") ? link : null);

export default function LedgerTab({ customerId, reload }) {
  const navigate = useNavigate();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [applied, setApplied] = useState({ from: "", to: "" });
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get(`/customers/${customerId}/ledger`, { params: { date_from: applied.from, date_to: applied.to } }).then(({ data }) => setData(data));
  }, [customerId, applied, reload]);

  return (
    <div className="panel overflow-hidden" data-testid="ledger-tab">
      <form onSubmit={(e) => { e.preventDefault(); setApplied({ from, to }); }} className="flex flex-wrap items-end gap-2 p-3 border-b border-slate-200">
        <div><div className="text-[11px] text-slate-500 mb-1">Başlangıç</div><Input type="date" className="h-9 w-40" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="ledger-date-from" /></div>
        <div><div className="text-[11px] text-slate-500 mb-1">Bitiş</div><Input type="date" className="h-9 w-40" value={to} onChange={(e) => setTo(e.target.value)} data-testid="ledger-date-to" /></div>
        <Button type="submit" size="sm" variant="outline" className="h-9" data-testid="ledger-filter-button"><Filter className="h-3.5 w-3.5 mr-1" />Filtrele</Button>
        <div className="flex flex-wrap gap-1" data-testid="ledger-presets">{PRESETS.map(([l, fn]) => <Button key={l} type="button" size="sm" variant="ghost" className="h-9 px-2 text-[12px]"
          onClick={() => { const [a, b] = fn(); setFrom(a); setTo(b); setApplied({ from: a, to: b }); }} data-testid={`ledger-preset-${l}`}>{l}</Button>)}</div>
        {(applied.from || applied.to) && <Button type="button" size="sm" variant="ghost" className="h-9" onClick={() => { setFrom(""); setTo(""); setApplied({ from: "", to: "" }); }} data-testid="ledger-filter-clear">Temizle</Button>}
        <div className="ml-auto"><StatementShare key={`${applied.from}|${applied.to}`} id={customerId} range={applied} /></div>
      </form>
      <div className="overflow-x-auto">
        <table className="erp-table" data-testid="ledger-table">
          <thead><tr><th>Tarih</th><th>Belge No</th><th>Tür</th><th>Açıklama</th><th>Vade</th><th className="text-right">Borç</th><th className="text-right">Alacak</th><th className="text-right">Bakiye</th></tr></thead>
          <tbody>
            {!data ? <LoadingRow cols={8} /> : (
              <>
                <tr className="bg-slate-50/70"><td colSpan={7} className="font-semibold text-slate-600">Devreden bakiye</td><td className="text-right font-mono font-semibold" data-testid="ledger-opening">{formatBalance(data.opening_balance)}</td></tr>
                {data.entries.length === 0 ? <EmptyRow cols={8} text="Bu aralıkta hareket yok" /> : data.entries.map((e) => (
                  <tr key={e.id} data-testid="ledger-row" data-link={detail(e.link) || ""} onClick={(ev) => detail(e.link) && !ev.target.closest("a") && navigate(detail(e.link))}
                    className={detail(e.link) ? "cursor-pointer hover:bg-blue-50/60" : ""}>
                    <td className="font-mono text-xs">{formatIsoDay(e.date)}</td>
                    <td className="font-mono text-xs"><DocLink link={e.link} testid={`ledger-doc-link-${e.doc_no}`}>{e.doc_no}</DocLink></td>
                    <td><span className="text-[11px] px-1.5 py-0.5 rounded border bg-slate-50 border-slate-200">{DOC[e.doc_type] || e.doc_type}</span></td>
                    <td className="max-w-[320px] truncate" title={e.description}>{e.description}</td>
                    <td className="font-mono text-xs">{formatIsoDay(e.due_date)}</td>
                    <td className="text-right font-mono">{e.debit ? formatTRY(e.debit) : ""}</td>
                    <td className="text-right font-mono">{e.credit ? formatTRY(e.credit) : ""}</td>
                    <td className="text-right font-mono font-semibold" data-testid="ledger-balance">{formatBalance(e.balance)}</td>
                  </tr>
                ))}
                <tr className="bg-slate-100/80 font-semibold">
                  <td colSpan={5} className="text-slate-700">Toplam / Kapanış</td>
                  <td className="text-right font-mono" data-testid="ledger-total-debit">{formatTRY(data.total_debit)}</td>
                  <td className="text-right font-mono" data-testid="ledger-total-credit">{formatTRY(data.total_credit)}</td>
                  <td className="text-right font-mono text-slate-900" data-testid="ledger-closing">{formatBalance(data.closing_balance)}</td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
      <div className="px-3 py-2 text-[11px] text-slate-400 border-t border-slate-200">(B) Borç bakiyesi · (A) Alacak bakiyesi</div>
    </div>
  );
}
